import { useEffect, useMemo, useState } from 'react';
import type { ComponentType, CSSProperties } from 'react';
import {
  FiCheck,
  FiChevronRight,
  FiFileText,
  FiFolder,
  FiGitBranch,
  FiGithub,
  FiSearch
} from 'react-icons/fi';

import {
  isScopePathIncluded,
  type LaunchState
} from '../workflowShared';
import {
  runFormatInstallationName,
  runFormatNumber,
  runGetScopeAncestorPaths,
  runGetScopeParentPath,
  type GitHubAppConfigResponse,
  type GitHubInstallationSummary,
  type GitHubLoadState,
  type GitHubRepositoryBranchSummary,
  type GitHubRepositoryChoice,
  type GitHubRepositoryDirectorySummary
} from './helpers/runScreenTypes';

type RunScreenIconComponent = ComponentType<{ 'aria-hidden'?: boolean }>;

const IconCheck = FiCheck as unknown as RunScreenIconComponent;
const IconChevronRight = FiChevronRight as unknown as RunScreenIconComponent;
const IconFileText = FiFileText as unknown as RunScreenIconComponent;
const IconFolder = FiFolder as unknown as RunScreenIconComponent;
const IconGitBranch = FiGitBranch as unknown as RunScreenIconComponent;
const IconGithub = FiGithub as unknown as RunScreenIconComponent;
const IconSearch = FiSearch as unknown as RunScreenIconComponent;

function RunScopeTreeChevron({ expanded }: { expanded: boolean }) {
  return (
    <span className={`scope-tree-chevron ${expanded ? 'expanded' : ''}`} aria-hidden={true}>
      <IconChevronRight aria-hidden={true} />
    </span>
  );
}

function RunScopeFolderTree({
  githubTreeState,
  githubTreeError,
  directories,
  scopePaths,
  onSelectRepositoryRoot,
  onToggleScopePath
}: {
  githubTreeState: GitHubLoadState;
  githubTreeError: string;
  directories: GitHubRepositoryDirectorySummary[];
  scopePaths: string[];
  onSelectRepositoryRoot: () => void;
  onToggleScopePath: (scopePath: string) => void;
}) {
  const [isRootExpanded, setIsRootExpanded] = useState(true);
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(() => new Set());
  const isRootSelected = scopePaths.length === 0;
  const selectedFolderCount = isRootSelected ? directories.length : scopePaths.length;
  const directoryPathSet = useMemo(() => {
    return new Set(directories.map((directory) => directory.path));
  }, [directories]);
  const directoryPathsWithChildren = useMemo(() => {
    const pathsWithChildren = new Set<string>();

    directories.forEach((directory) => {
      const parentPath = runGetScopeParentPath(directory.path);

      if (parentPath && directoryPathSet.has(parentPath)) {
        pathsWithChildren.add(parentPath);
      }
    });

    return pathsWithChildren;
  }, [directories, directoryPathSet]);
  const visibleDirectories = useMemo(() => {
    if (!isRootExpanded) {
      return [];
    }

    return directories.filter((directory) => {
      return runGetScopeAncestorPaths(directory.path).every((ancestorPath) => expandedPaths.has(ancestorPath));
    });
  }, [directories, expandedPaths, isRootExpanded]);

  useEffect(() => {
    setIsRootExpanded(true);
    setExpandedPaths((currentExpandedPaths) => {
      const nextExpandedPaths = new Set<string>();

      currentExpandedPaths.forEach((expandedPath) => {
        if (directoryPathSet.has(expandedPath)) {
          nextExpandedPaths.add(expandedPath);
        }
      });

      return nextExpandedPaths;
    });
  }, [directoryPathSet]);

  useEffect(() => {
    if (scopePaths.length === 0) {
      return;
    }

    setIsRootExpanded(true);
    setExpandedPaths((currentExpandedPaths) => {
      const nextExpandedPaths = new Set(currentExpandedPaths);

      scopePaths.forEach((scopePath) => {
        runGetScopeAncestorPaths(scopePath).forEach((ancestorPath) => {
          if (directoryPathSet.has(ancestorPath)) {
            nextExpandedPaths.add(ancestorPath);
          }
        });
      });

      return nextExpandedPaths;
    });
  }, [scopePaths, directoryPathSet]);

  const toggleDirectoryExpansion = (scopePath: string) => {
    setExpandedPaths((currentExpandedPaths) => {
      const nextExpandedPaths = new Set(currentExpandedPaths);

      if (nextExpandedPaths.has(scopePath)) {
        nextExpandedPaths.delete(scopePath);
      } else {
        nextExpandedPaths.add(scopePath);
      }

      return nextExpandedPaths;
    });
  };

  return (
    <div className="repository-scope-picker">
      <div className="scope-folders-heading">
        <span>Folders</span>
        <div>
          <button type="button" onClick={onSelectRepositoryRoot}>Select all</button>
          <span aria-hidden={true} />
          <button type="button" onClick={onSelectRepositoryRoot}>Clear</button>
        </div>
      </div>
      <div className="scope-tree" role="tree" aria-label="Repository folders">
        <div
          role="treeitem"
          aria-level={1}
          aria-expanded={directories.length > 0 ? isRootExpanded : undefined}
          aria-selected={isRootSelected}
          className={`scope-tree-row root ${isRootSelected ? 'selected' : ''}`}
        >
          <button
            type="button"
            className={`scope-tree-disclosure ${directories.length === 0 ? 'placeholder' : ''}`}
            aria-label={isRootExpanded ? 'Collapse repository root' : 'Expand repository root'}
            onClick={() => setIsRootExpanded((expanded) => !expanded)}
            disabled={directories.length === 0}
          >
            <RunScopeTreeChevron expanded={isRootExpanded} />
          </button>
          <button type="button" className="scope-tree-select" onClick={onSelectRepositoryRoot}>
            <span className={`scope-tree-check ${isRootSelected ? 'checked' : ''}`} aria-hidden={true} />
            <IconFolder aria-hidden={true} />
            <span className="scope-tree-name">Repository root</span>
            <span className="scope-tree-meta">{directories.length} folders</span>
          </button>
        </div>

        {githubTreeState === 'loading' && (
          <p className="codebase-status scope-tree-status">Loading folders...</p>
        )}
        {githubTreeState === 'error' && (
          <p className="codebase-status error scope-tree-status">{githubTreeError}</p>
        )}
        {githubTreeState === 'ready' && directories.length === 0 && (
          <p className="codebase-status scope-tree-status">No folders found.</p>
        )}
        {githubTreeState === 'ready' && visibleDirectories.map((directory) => {
          const hasChildren = directoryPathsWithChildren.has(directory.path);
          const isExpanded = expandedPaths.has(directory.path);
          const isSelected = isRootSelected || isScopePathIncluded(scopePaths, directory.path);
          const isExactSelection = !isRootSelected && scopePaths.includes(directory.path);

          return (
            <div
              role="treeitem"
              aria-level={directory.depth + 1}
              aria-expanded={hasChildren ? isExpanded : undefined}
              aria-selected={isSelected}
              className={`scope-tree-row ${isSelected ? 'selected' : ''} ${isExactSelection ? 'exact' : ''}`}
              key={directory.path}
              style={{ '--tree-depth': Math.max(0, directory.depth - 1) } as CSSProperties}
            >
              <button
                type="button"
                className={`scope-tree-disclosure ${hasChildren ? '' : 'placeholder'}`}
                aria-label={isExpanded ? `Collapse ${directory.name}` : `Expand ${directory.name}`}
                onClick={() => toggleDirectoryExpansion(directory.path)}
                disabled={!hasChildren}
              >
                <RunScopeTreeChevron expanded={isExpanded} />
              </button>
              <button
                type="button"
                className="scope-tree-select"
                onClick={() => onToggleScopePath(directory.path)}
              >
                <span className={`scope-tree-check ${isSelected ? 'checked' : ''}`} aria-hidden={true} />
                <IconFolder aria-hidden={true} />
                <span className="scope-tree-name">{directory.name}</span>
                <span className="scope-tree-meta">{isExactSelection ? 'selected' : isSelected ? 'included' : ''}</span>
              </button>
            </div>
          );
        })}
      </div>
      <span className="scope-folder-count">
        {isRootSelected ? 'Full codebase' : `${runFormatNumber(selectedFolderCount)} selected`}
      </span>
    </div>
  );
}

export function RunScopeScreen({
  githubConfigState,
  githubConfigError,
  githubConfig,
  githubSessionState,
  githubSessionError,
  githubSessionId,
  githubInstallations,
  selectedGithubInstallationId,
  githubRepositoriesState,
  githubRepositoriesError,
  githubRepositoryChoices,
  selectedGithubRepositoryId,
  githubBranchesState,
  githubBranchesError,
  githubBranches,
  selectedGithubBranch,
  githubTreeState,
  githubTreeError,
  githubTreeDirectories,
  scopePaths,
  assumptionsFilePath,
  workspaceScopeSaveState,
  workspaceScopeSaveError,
  onConnectGitHub,
  onInstallGitHubApp,
  onSelectGithubRepository,
  onSelectGithubBranch,
  onSelectRepositoryRootScope,
  onToggleScopePath,
  onAssumptionsFilePathChange,
  onReviewPricing
}: {
  githubConfigState: GitHubLoadState;
  githubConfigError: string;
  githubConfig: GitHubAppConfigResponse | null;
  githubSessionState: GitHubLoadState;
  githubSessionError: string;
  githubSessionId: string;
  githubInstallations: GitHubInstallationSummary[];
  selectedGithubInstallationId: number | null;
  githubRepositoriesState: GitHubLoadState;
  githubRepositoriesError: string;
  githubRepositoryChoices: GitHubRepositoryChoice[];
  selectedGithubRepositoryId: number | null;
  githubBranchesState: GitHubLoadState;
  githubBranchesError: string;
  githubBranches: GitHubRepositoryBranchSummary[];
  selectedGithubBranch: string;
  githubTreeState: GitHubLoadState;
  githubTreeError: string;
  githubTreeDirectories: GitHubRepositoryDirectorySummary[];
  scopePaths: string[];
  assumptionsFilePath: string;
  workspaceScopeSaveState: LaunchState;
  workspaceScopeSaveError: string;
  onConnectGitHub: () => void;
  onInstallGitHubApp: () => void;
  onSelectGithubRepository: (installationId: number, repositoryId: number) => void;
  onSelectGithubBranch: (branch: string) => void;
  onSelectRepositoryRootScope: () => void;
  onToggleScopePath: (scopePath: string) => void;
  onAssumptionsFilePathChange: (value: string) => void;
  onReviewPricing: () => void;
}) {
  const selectedGithubInstallation = githubInstallations.find((installation) => {
    return installation.id === selectedGithubInstallationId;
  });
  const hasGitHubInstallUrl = Boolean(githubConfig?.installUrl ?? selectedGithubInstallation?.htmlUrl);
  const githubActionLabel = githubSessionId ? 'Access repo' : 'Login with GitHub';
  const githubAction = githubSessionId ? onInstallGitHubApp : onConnectGitHub;
  const githubActionDisabled = githubSessionId ? !hasGitHubInstallUrl : !githubConfig?.connectUrl;
  const showGitHubAction = !githubSessionId || githubInstallations.length === 0;
  const [repositoryQuery, setRepositoryQuery] = useState('');
  const filteredRepositoryChoices = useMemo(() => {
    const query = repositoryQuery.trim().toLowerCase();

    if (!query) {
      return githubRepositoryChoices;
    }

    return githubRepositoryChoices.filter(({ installation, repository }) => {
      return [
        runFormatInstallationName(installation),
        repository.fullName,
        repository.owner,
        repository.repo
      ].some((value) => value.toLowerCase().includes(query));
    });
  }, [githubRepositoryChoices, repositoryQuery]);
  const selectedBranchInfo = githubBranches.find((branch) => branch.name === selectedGithubBranch);
  const selectedCommitLabel = selectedBranchInfo?.commitSha
    ? selectedBranchInfo.commitSha.slice(0, 7)
    : selectedBranchInfo?.default
      ? 'default'
      : '';
  const canSetScope = Boolean(selectedGithubRepositoryId && selectedGithubBranch);
  const showScopeStatusStack = showGitHubAction ||
    githubConfigState === 'loading' ||
    githubConfigState === 'error' ||
    Boolean(githubConfigState === 'ready' && githubConfig && !githubConfig.configured) ||
    Boolean(githubSessionError) ||
    Boolean(workspaceScopeSaveError) ||
    githubSessionState === 'loading' ||
    Boolean(githubSessionId && githubSessionState === 'ready' && githubInstallations.length === 0);

  return (
    <main className="run-scope-page">
      <section className="run-codebase-section scope-shell" aria-label="Codebase scope">
        <div className="scope-shell-topbar">
          <h1>Scope</h1>
          {githubSessionId && githubSessionState === 'ready' && githubInstallations.length > 0 && (
            <button
              type="button"
              className="primary-button scope-set-button"
              disabled={!canSetScope || workspaceScopeSaveState === 'submitting'}
              onClick={onReviewPricing}
            >
              {workspaceScopeSaveState === 'submitting' ? 'Saving...' : 'Set scope'}
            </button>
          )}
        </div>

        {showScopeStatusStack && (
          <div className="scope-status-stack">
            {showGitHubAction && (
              <button
                type="button"
                className="primary-button github-access-button"
                onClick={githubAction}
                disabled={githubActionDisabled}
              >
                <IconGithub aria-hidden={true} />
                <span>{githubActionLabel}</span>
              </button>
            )}
            {githubConfigState === 'loading' && <p className="codebase-status">Loading GitHub App configuration...</p>}
            {githubConfigState === 'error' && <p className="codebase-status error">{githubConfigError}</p>}
            {githubConfigState === 'ready' && githubConfig && !githubConfig.configured && (
              <p className="codebase-status error">
                Missing GitHub App config: {(githubConfig.missing ?? []).join(', ')}
              </p>
            )}
            {githubSessionError && <p className="codebase-status error">{githubSessionError}</p>}
            {workspaceScopeSaveError && <p className="codebase-status error">{workspaceScopeSaveError}</p>}
            {githubSessionState === 'loading' && <p className="codebase-status">Loading GitHub installations...</p>}
            {githubSessionId && githubSessionState === 'ready' && githubInstallations.length === 0 && (
              <p className="codebase-status">No GitHub App installations found.</p>
            )}
          </div>
        )}

        {githubSessionId && githubSessionState === 'ready' && githubInstallations.length > 0 && (
          <>
            <div className="scope-shell-body">
              <aside className="scope-repository-rail" aria-label="Repositories">
                <div className="scope-repository-search">
                  <label htmlFor="scope-repository-search">Repository</label>
                  <div>
                    <IconSearch aria-hidden={true} />
                    <input
                      id="scope-repository-search"
                      type="text"
                      value={repositoryQuery}
                      placeholder="Search repositories"
                      onChange={(event) => setRepositoryQuery(event.target.value)}
                    />
                  </div>
                </div>

                {githubRepositoriesState === 'loading' && <p className="codebase-status">Loading repositories...</p>}
                {githubRepositoriesState === 'error' && <p className="codebase-status error">{githubRepositoriesError}</p>}
                {githubInstallations.length > 0 && githubRepositoriesState === 'ready' && githubRepositoryChoices.length === 0 && (
                  <p className="codebase-status">No repositories available.</p>
                )}

                <div className="scope-repository-list" role="listbox" aria-label="GitHub repositories">
                  {filteredRepositoryChoices.map(({ installation, repository }) => {
                    const isSelected = installation.id === selectedGithubInstallationId &&
                      repository.id === selectedGithubRepositoryId;

                    return (
                      <button
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        className={`scope-repository-row ${isSelected ? 'selected' : ''}`}
                        key={`${String(installation.id)}-${String(repository.id)}`}
                        onClick={() => onSelectGithubRepository(installation.id, repository.id)}
                      >
                        <IconFileText aria-hidden={true} />
                        <span>
                          <strong>{repository.fullName}</strong>
                          <small>{repository.private ? 'Private' : 'Public'} · {runFormatInstallationName(installation)}</small>
                        </span>
                        {isSelected && <IconCheck aria-hidden={true} />}
                      </button>
                    );
                  })}
                  {githubRepositoriesState === 'ready' && githubRepositoryChoices.length > 0 && filteredRepositoryChoices.length === 0 && (
                    <span className="scope-empty-note">No repositories match.</span>
                  )}
                </div>
              </aside>

              <div className="scope-main-pane">
                {selectedGithubRepositoryId && githubRepositoriesState === 'ready' ? (
                  <>
                    <div className="scope-branch-panel">
                      <span>Branch</span>
                      <div className="scope-branch-row">
                        <label className="scope-branch-select">
                          <IconGitBranch aria-hidden={true} />
                          <select
                            value={selectedGithubBranch}
                            onChange={(event) => onSelectGithubBranch(event.target.value)}
                            disabled={githubBranchesState !== 'ready' || githubBranches.length === 0}
                          >
                            {githubBranches.length === 0 ? (
                              <option value="">No branches loaded</option>
                            ) : githubBranches.map((branch) => (
                              <option key={branch.name} value={branch.name}>
                                {branch.default ? `${branch.name} (default)` : branch.name}
                              </option>
                            ))}
                          </select>
                        </label>
                        {selectedCommitLabel && <span className="scope-commit-line">{selectedCommitLabel}</span>}
                      </div>
                      {githubBranchesState === 'loading' && <p className="codebase-status">Loading branches...</p>}
                      {githubBranchesState === 'error' && <p className="codebase-status error">{githubBranchesError}</p>}
                    </div>

                    <RunScopeFolderTree
                      githubTreeState={githubTreeState}
                      githubTreeError={githubTreeError}
                      directories={githubTreeDirectories}
                      scopePaths={scopePaths}
                      onSelectRepositoryRoot={onSelectRepositoryRootScope}
                      onToggleScopePath={onToggleScopePath}
                    />

                    <label className="scope-assumptions-field">
                      <span>Assumptions file</span>
                      <input
                        type="text"
                        value={assumptionsFilePath}
                        placeholder="docs/assumptions.md"
                        onChange={(event) => onAssumptionsFilePathChange(event.target.value)}
                      />
                    </label>
                  </>
                ) : (
                  <div className="scope-empty-pane">
                    Select a repository.
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </section>
    </main>
  );
}
